import type { ComponentPropsWithRef, ReactNode } from 'react';

import { cn } from '../lib/cn';

// `ComponentPropsWithRef` : React 19 passe `ref` comme prop normale, ce qui laisse
// le champ pilotable depuis l'extérieur (focus programmatique, adaptateurs de formulaire).
export type InputProps = Omit<ComponentPropsWithRef<'input'>, 'size'> & {
  invalid?: boolean;
  /** Icône décorative à gauche (24px dans la maquette). */
  icon?: ReactNode;
  /** Contenu interactif à droite : bouton « effacer », unité… */
  trailing?: ReactNode;
  /** Classe appliquée au conteneur (bordure, largeur) plutôt qu'au `<input>`. */
  containerClassName?: string;
};

/**
 * L'`<input>` occupe toute la boîte (padding compris) : un clic n'importe où
 * dans le cadre place le curseur. L'icône est en superposition, non cliquable.
 */
export function Input({
  className,
  containerClassName,
  invalid,
  icon,
  trailing,
  disabled,
  readOnly,
  ...props
}: InputProps) {
  // Un champ en lecture seule reste focusable et copiable, mais il doit se
  // voir comme inactif : sinon l'utilisateur tape dedans sans comprendre
  // pourquoi rien ne s'écrit.
  const inerte = disabled || readOnly;

  return (
    <div
      className={cn(
        'relative flex h-[53px] w-full items-center rounded-field border bg-surface transition-colors',
        'border-border-input/50 focus-within:border-brand-600 focus-within:ring-2 focus-within:ring-brand-600/15',
        invalid && 'border-danger focus-within:border-danger focus-within:ring-danger/15',
        inerte && 'bg-surface-muted opacity-70',
        disabled && 'cursor-not-allowed',
        // En lecture seule, le cadre ne réagit plus au focus : rien n'y est
        // modifiable, l'anneau de saisie serait un faux signal.
        readOnly && 'border-border-subtle focus-within:border-border-subtle focus-within:ring-0',
        containerClassName,
      )}
    >
      {icon ? (
        <span
          aria-hidden="true"
          className={cn(
            'pointer-events-none absolute left-3 flex text-content-muted',
            invalid && 'text-danger',
          )}
        >
          {icon}
        </span>
      ) : null}

      <input
        aria-invalid={invalid || undefined}
        disabled={disabled}
        readOnly={readOnly}
        className={cn(
          'h-full w-full min-w-0 rounded-field bg-transparent text-sm font-medium text-content outline-none',
          'placeholder:font-medium placeholder:text-content-subtle disabled:cursor-not-allowed',
          readOnly && 'cursor-default text-content-muted',
          icon ? 'pl-12' : 'pl-3',
          trailing ? 'pr-12' : 'pr-3',
          className,
        )}
        {...props}
      />

      {trailing ? <span className="absolute right-2 flex items-center">{trailing}</span> : null}
    </div>
  );
}
